import { isPlatformBrowser } from '@angular/common';
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { ConnectionStatus } from '@capacitor/network';
import { BASE_API_URL, IUser } from '@curbnturf/entities';
import { NetworkState } from '@curbnturf/network/src/lib/network/+state/network.reducer';
import * as NetworkSelectors from '@curbnturf/network/src/lib/network/+state/network.selectors';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { first, skipWhile, switchMap, withLatestFrom } from 'rxjs/operators';
import { AuthFacade } from './+state/auth.facade';
import { AuthService } from './auth.service';

const jwtInterceptorAlreadyInterceptors = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  injectors: {
    authFacade: AuthFacade;
    authService: AuthService;
    store: Store<NetworkState>;
    platformId: Object;
  },
): Observable<HttpEvent<unknown>> => {
  // add authorization header with jwt token if available
  return injectors.authFacade.user$.pipe(
    first(),
    withLatestFrom(injectors.store.select(NetworkSelectors.getStatus)),
    switchMap(([currentUser, status]: [IUser, ConnectionStatus]) => {
      if (
        currentUser &&
        (!currentUser.authExpires ||
          // if the auth is expired or will expire in the next 30 seconds to allow for slow internet
          currentUser.authExpires < DateTime.now().toMillis() + 30000) &&
        status?.connected === true // If not connected we don't want to try to renew the token.
      ) {
        setTimeout(() => injectors.authService.renewSubject.next('renew'));
        return injectors.authService.renew$.pipe(
          skipWhile((command) => command !== 'success' && command !== 'deferred'),
          first(),
          // if we renew the token we need to call the jwtInterceptor again to get the new token and add it to the request
          switchMap(() => jwtInterceptorAlreadyInterceptors(request, next, injectors)),
        );
      } else if (currentUser?.authIdToken) {
        return next(
          request.clone({
            setHeaders: {
              Authorization: currentUser.authIdToken,
            },
          }),
        );
      } else {
        return next(request);
      }
    }),
  );
};

export const jwtInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn) => {
  const authFacade = inject(AuthFacade);
  const authService = inject(AuthService);
  const store = inject(Store<NetworkState>);
  const platformId = inject(PLATFORM_ID);

  if (isPlatformBrowser(platformId) && request.url.includes(BASE_API_URL)) {
    // add authorization header with jwt token if available
    return jwtInterceptorAlreadyInterceptors(request, next, { authFacade, authService, store, platformId });
  }

  return next(request);
};
